class CoucouLevelLearnSlider {
  constructor(section) {
    this.section = section;
    this.slider = section.querySelector('.coucou-level-learn__swiper');
    this.wrapper = section.querySelector('.swiper-wrapper');
    this.navigation = section.querySelector('.coucou-level-learn__navigation');
    this.fraction = section.querySelector('.coucou-level-learn__fraction');
    this.prevButton = section.querySelector('.coucou-level-learn__nav-btn--prev');
    this.nextButton = section.querySelector('.coucou-level-learn__nav-btn--next');
    this.hasSeeAll = Boolean(section.querySelector('.coucou-level-learn__see-all-btn'));
    
    // Find the container that holds both fraction and buttons
    this.navLeft = section.querySelector('.coucou-level-learn__navigation__left');
    
    this.swiper = null;
    
    // Bind methods
    this.updateLayout = this.updateLayout.bind(this);
    this.updateFraction = this.updateFraction.bind(this);

    if (!this.slider) return;
    this.init();
  }

  init() {
    if (this.slider.swiper) {
      this.slider.swiper.destroy(true, true);
    }

    this.swiper = new Swiper(this.slider, {
      slidesPerView: 1,
      spaceBetween: 12,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      navigation: {
        prevEl: this.prevButton,
        nextEl: this.nextButton,
      },

      breakpoints: {
        768: {
          slidesPerView: 2,
          spaceBetween: 20,
        },
        1025: {
          slidesPerView: 4,
          spaceBetween: 12,
        },
      },

      on: {
        init: () => {
          this.updateFraction();
          this.updateLayout();
        },
        slideChange: () => {
          this.updateFraction();
        },
        resize: () => {
          this.updateFraction();
          this.updateLayout();
        },
        breakpoint: () => {
          this.updateFraction();
          this.updateLayout();
        },
        observerUpdate: () => {
          this.updateLayout();
        },
      },
    });

    window.addEventListener('load', this.updateLayout);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(this.updateLayout);
    }
  }

  /* ================================================================== */

  syncTitleHeights() {
    if (!this.section) return;

    const titles = Array.from(this.section.querySelectorAll('.coucou-level-learn__card__title'));
    if (!titles.length) return;

    titles.forEach((title) => {
      title.style.minHeight = '';
    });

    const maxTitleHeight = titles.reduce((max, title) => {
      const height = Math.ceil(title.getBoundingClientRect().height);
      return height > max ? height : max;
    }, 0);

    if (maxTitleHeight > 0) {
      titles.forEach((title) => {
        title.style.minHeight = `${maxTitleHeight}px`;
      });
    }
  }

  /* ================================================================== */

  lockWrapperHeight() {
    if (!this.wrapper || !this.swiper) return;
    this.wrapper.style.minHeight = '';

    const realSlides = Array.from(this.swiper.slides).filter((s) => !s.classList.contains('swiper-slide-duplicate'));
    let maxH = 0;
    realSlides.forEach((slide) => {
      const card = slide.querySelector('.coucou-level-learn__card__inner');
      if (!card) return;

      const h = Math.ceil(card.getBoundingClientRect().height);
      if (h > maxH) maxH = h;
    });

    if (maxH > 0) {
      const button = this.section.querySelector('.coucou-level-learn__card__button');
      const buttonH = button ? Math.ceil(button.getBoundingClientRect().height) : 0;
      this.wrapper.style.minHeight = `${maxH + 8 + buttonH}px`;
    }
  }

  /* ================================================================== */

  updateLayout() {
    this.syncTitleHeights();
    this.lockWrapperHeight();
  }

  /* 
   * FIXED: This method now uses requestAnimationFrame to ensure
   * the layout is fully settled before checking the number of slides.
   */
  updateFraction() {
    if (!this.swiper) return;
    
    // Defer execution until the next paint cycle
    requestAnimationFrame(() => {
      if (!this.swiper) return;

      // Force Swiper to recalculate its internal state (snapGrid)
      this.swiper.update();

      const total = this.swiper.snapGrid.length || 1;
      const current = Math.min((this.swiper.snapIndex || 0) + 1, total);

      if (this.fraction) {
        this.fraction.textContent = `[ ${current} / ${total} ]`;
      }

      if (this.navLeft) {
        // If only 1 snap point, everything fits on screen -> Hide Nav
        if (total <= 1) {
          this.navLeft.classList.add('coucou-level-learn__navigation__left--hidden');
          this.navLeft.style.display = 'none';
          if (this.navigation && !this.hasSeeAll) {
            this.navigation.classList.add('coucou-level-learn__navigation--empty');
          }
        } else {
          // Otherwise, show Nav
          if (this.navigation) {
            this.navigation.classList.remove('coucou-level-learn__navigation--empty');
          }
          this.navLeft.classList.remove('coucou-level-learn__navigation__left--hidden');
          if (window.matchMedia('(min-width: 768px) and (max-width: 1025px)').matches) {
            this.navLeft.style.display = '';
          } else {
            // We use 'flex' explicitly to ensure it acts as a container for the buttons
            this.navLeft.style.display = 'flex';
          }
        }
      }
    });
  }

  destroy() {
    window.removeEventListener('load', this.updateLayout);

    if (this.wrapper) {
      this.wrapper.style.minHeight = '';
    }

    this.section.querySelectorAll('.coucou-level-learn__card__title').forEach((title) => {
      title.style.minHeight = '';
    });

    if (this.navigation) {
      this.navigation.classList.remove('coucou-level-learn__navigation--empty');
    }

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  /* ================================================================== */

  static initAll(root = document) {
    const sections = root.querySelectorAll
      ? root.querySelectorAll('.coucou-level-learn__section')
      : document.querySelectorAll('.coucou-level-learn__section');

    sections.forEach((section) => {
      if (section.coucouLevelLearnSliderInstance) {
        section.coucouLevelLearnSliderInstance.destroy();
      }
      section.coucouLevelLearnSliderInstance = new CoucouLevelLearnSlider(section);
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  CoucouLevelLearnSlider.initAll();
});

document.addEventListener('shopify:section:load', (event) => {
  if (event.target) {
    CoucouLevelLearnSlider.initAll(event.target);
  } else {
    CoucouLevelLearnSlider.initAll();
  }
});
